/* eslint-disable max-len */
import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { Formik } from 'formik';
import moment from 'moment';
import { useHistory } from 'react-router-dom';
import Button from '../button/Button';
import DatePickerField from '../datePickerField/DatePickerField';
import Input from '../input/Input';
import {
  getUserLoggedIn, schedulePage, getOnboardingSkip, arrivalTimePage,
} from '../../utils';
import { virtualPageView } from '../../googleTagManager';
import './Login.scss';

const calculateLoginMessage = (isLoginDisabled) => {
  if (isLoginDisabled) return 'Sorry the system is offline at the moment';
  return 'We\'ll send you an email letting you know when you can log in and book.';
};

const hasError = (errors = {}) => {
  if (!errors) return false;
  return (errors.ref && errors.ref.length > 0) || (errors.lastName && errors.lastName.length > 0);
};

const Login = ({
  isFetching,
  login,
  booking,
  errorMessage,
  isLoginDisabled,
  secretCode,
}) => {
  const history = useHistory();
  const skipOnBoarding = getOnboardingSkip();
  const maxDate = moment().add(1, 'year').endOf('year').toDate();
  const userLoggedIn = getUserLoggedIn();

  useEffect(() => {
    virtualPageView('plan my day', 'identity', '/ga-virtual/plan-my-day/identity');
  }, []);

  useEffect(() => {
    if (userLoggedIn) {
      history.push(schedulePage);
    }
  }, [userLoggedIn, history]);

  useEffect(() => {
    if (!isFetching && booking && booking.Units && errorMessage === '') {
      if (skipOnBoarding) {
        history.push(schedulePage);
      } else {
        history.push(arrivalTimePage);
      }
    }
  }, [history, isFetching, booking, errorMessage, skipOnBoarding]);

  const validateForm = (values) => {
    const errors = {};
    if (!values.ref) {
      errors.ref = 'Required';
    }
    if (!/^[BMS]{1}[GHK]{1}([0-9]{7}|[0-9]{8})$/i.test(values.ref)) {
      errors.ref = 'Invalid format';
    }
    if (!values.arrivalDate) {
      errors.arrivalDate = 'Required';
    }
    if (!values.lastName) {
      errors.lastName = 'Required';
    }
    return errors;
  };

  const onSubmit = (values) => {
    const siteCode = values.ref.slice(0, 2);
    const refId = values.ref.slice(2, 10);
    login(refId, siteCode, values.lastName, values.arrivalDate, values.ref, secretCode);
  };

  return (
    <div className="login">
      <div className="login-container">
        <div className="login-title">
          <h2>Log in to book your experience</h2>
          <h3>{calculateLoginMessage(isLoginDisabled)}</h3>
          <div className="textInfo">
            You can use this to book some of our activities and shows in our Studio 36 and Centre Stage venues. Please use our app to book all other activities. All shows in our Reds and Skyline Stage venues are freeflow.
          </div>
        </div>
        <Formik
          initialValues={{ ref: '', arrivalDate: null, lastName: '' }}
          validate={values => validateForm(values)}
          onSubmit={(values) => {
            onSubmit(values);
          }}
        >
          {({
            values,
            errors,
            touched,
            handleChange,
            handleBlur,
            handleSubmit,
          }) => (
            <form onSubmit={handleSubmit}>
              <Input
                type="text"
                name="ref"
                placeholder="BG123456, SK123456, MH123456"
                onChange={handleChange}
                onBlur={handleBlur}
                value={values.ref}
                error={errors.ref && touched.ref && errors.ref}
              />
              <DatePickerField
                name="arrivalDate"
                value={values.arrivalDate}
                maxDate={maxDate}
                disabled={isLoginDisabled}
                placeholderText="Arrival date"
                error={errors.arrivalDate && touched.arrivalDate && errors.arrivalDate}
              />
              <Input
                type="text"
                name="lastName"
                placeholder="Lead guest surname"
                onChange={handleChange}
                onBlur={handleBlur}
                value={values.lastName}
                error={errors.lastName && touched.lastName && errors.lastName}
              />
              <Button
                type="submit"
                text="Login"
                disabled={isFetching || isLoginDisabled || hasError(errors)}
              />
            </form>
          )}
        </Formik>
      </div>
    </div>
  );
};

Login.propTypes = {
  login: PropTypes.func.isRequired,
  isFetching: PropTypes.bool.isRequired,
  errorMessage: PropTypes.string.isRequired,
  booking: PropTypes.object.isRequired,
  isLoginDisabled: PropTypes.bool,
  secretCode: PropTypes.string,
};

export default Login;
